import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar.jsx';
import InfoBadge from '../components/InfoBadge.jsx';
import api from '../lib/api.js';

export default function Settings() {
  const [agol, setAgol] = useState(null);
  const [form, setForm] = useState({ org_url: '', username: '', password: '', publish_default: false });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [me, setMe] = useState(null);
  const [keyReveal, setKeyReveal] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    try {
      const d = await api.getAgol();
      setAgol(d.agol);
      setForm(f => ({
        org_url: d.agol.org_url || f.org_url,
        username: d.agol.username || f.username,
        password: '',
        publish_default: !!d.agol.publish_default,
      }));
    } catch (e) { setErr(e.message); }
    try {
      const m = await api.me();
      setMe(m.user || m);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { load(); }, []);

  const onRotateKey = async () => {
    if (!confirm('Rotate your access key? Any device or script using the old key will stop working until updated.')) return;
    setRotating(true);
    try {
      const r = await api.rotateMyKey();
      const newKey = r?.user?.access_key || r?.access_key;
      if (newKey) {
        sessionStorage.setItem('as_key', newKey);
        setMe(prev => ({ ...(prev || {}), access_key: newKey }));
        setKeyReveal(true);
        setMsg('Access key rotated. Copy the new value and update any clients.');
      }
    } catch (e) { setErr('Rotate failed: ' + e.message); }
    finally { setRotating(false); }
  };

  const onCopyKey = async () => {
    const k = me?.access_key || sessionStorage.getItem('as_key') || '';
    if (!k) return;
    try { await navigator.clipboard.writeText(k); setKeyCopied(true); setTimeout(() => setKeyCopied(false), 2000); }
    catch { /* clipboard blocked; user can still select text */ }
  };

  const accessKey = me?.access_key || sessionStorage.getItem('as_key') || '';
  const masked = accessKey ? accessKey.slice(0, 6) + '••••••••••••••••' + accessKey.slice(-4) : '';

  const onTest = async () => {
    setTesting(true); setErr(''); setMsg('');
    try {
      const r = await api.testAgol(form.password ? form : {});
      if (r.ok) setMsg(`Connected. Portal: ${r.portal}`);
      else setErr(r.error || 'Connection failed');
    } catch (e) { setErr(e.message); }
    finally { setTesting(false); }
  };
  const onSave = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      // Only send password if user typed a new one
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await api.saveAgol(payload);
      setMsg('Saved.');
      setForm(f => ({ ...f, password: '' }));
      load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="tag tag-gold mb-2">Settings</div>
          <h1 className="text-3xl font-bold text-navy">Your account</h1>
          <p className="text-sm text-ink-muted mt-1">Manage credentials and per-account preferences.</p>
        </div>

        <div className="card card-pad space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-navy">ArcGIS Online (AGOL)</h2>
            <InfoBadge title="What is this" body={'Saving your AGOL credentials lets MAPTRIUM publish each finished job’s GeoJSON as a hosted Feature Layer directly into YOUR AGOL org. The published item appears under your own account, uses your own credits, and is yours to share or delete. We never publish to a shared workspace.'} />
          </div>
          <p className="text-sm text-ink-muted">
            MAPTRIUM stores your password encrypted at rest (AES-256-GCM). It is only used to mint a short-lived AGOL token at publish time.
          </p>

          {agol?.has_password && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
              Configured. Connected as <strong>{agol.username}</strong> at <strong>{agol.org_url || 'arcgis.com'}</strong>. Saved password ends with <code className="font-mono">{agol.password_hint}</code>.
            </div>
          )}

          <div>
            <label className="label flex items-center gap-1">
              AGOL portal URL
              <InfoBadge title="Portal URL" body={'Your organization’s ArcGIS Online URL. For most users this is something like https://yourorg.maps.arcgis.com. If you only have a standard arcgis.com account leave blank or use https://www.arcgis.com.'} />
            </label>
            <input className="input" value={form.org_url} onChange={e => setForm({ ...form, org_url: e.target.value })} placeholder="https://yourorg.maps.arcgis.com" />
          </div>

          <div>
            <label className="label flex items-center gap-1">
              Username
              <InfoBadge title="AGOL username" body={'Your AGOL named-user account login. Service accounts are also fine. Must have Creator-level or higher to publish hosted Feature Layers.'} />
            </label>
            <input className="input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="myname_orgsuffix" autoComplete="off" />
            <div className="mt-1 text-xs text-amber-700">
              Heads up: ArcGIS usernames are <strong>case-sensitive</strong>. Copy the exact value from your AGOL profile page; uppercase and lowercase letters are not interchangeable.
            </div>
          </div>

          <div>
            <label className="label flex items-center gap-1">
              Password
              <InfoBadge title="Password" body={'Stored encrypted (AES-256-GCM). Replace any time by saving a new value here. Leave blank to keep the saved value.'} />
            </label>
            <input type="password" className="input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={agol?.has_password ? 'Leave blank to keep saved password' : '••••••••'} autoComplete="new-password" />
          </div>

          <label className="flex gap-2 items-center text-sm">
            <input type="checkbox" checked={form.publish_default} onChange={e => setForm({ ...form, publish_default: e.target.checked })} />
            <span>Publish every completed job to my AGOL automatically</span>
            <InfoBadge title="Auto publish" body={'If on, every successful job run also publishes the GeoJSON as a hosted Feature Layer in your AGOL. You can override per job in the Run step.'} />
          </label>

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
          {msg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{msg}</div>}

          <div className="flex gap-2 pt-2">
            <button className="btn btn-ghost" disabled={testing || !form.org_url || !form.username || !(form.password || agol?.has_password)} onClick={onTest}>{testing ? 'Testing…' : 'Test connection'}</button>
            <button className="btn btn-primary" disabled={saving || !form.org_url || !form.username} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>

        {/* Developer access — needed by MAPTRIUM Desktop, CLI tools, and admin scripts */}
        <div className="card card-pad space-y-4 mt-6">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-navy">Developer access</h2>
            <InfoBadge title="What is this" body={'Your access key authenticates non-browser clients (MAPTRIUM Desktop, the API CLI, mobile app sign-in) against the cloud. Treat it like a password. Send as the X-Admin-Key HTTP header along with X-Admin-Email set to your account email.'} />
          </div>
          <p className="text-sm text-ink-muted">
            Send this key as the <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">X-Admin-Key</code> header (paired with <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">X-Admin-Email</code>) on any request from MAPTRIUM Desktop, the API CLI, or your own scripts.
          </p>

          {!accessKey && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
              No access key on this session. Sign out and back in to refresh.
            </div>
          )}

          {accessKey && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 font-mono text-sm break-all select-all">
              {keyReveal ? accessKey : masked}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button className="btn btn-ghost" onClick={() => setKeyReveal(v => !v)} disabled={!accessKey}>
              {keyReveal ? 'Hide key' : 'Reveal key'}
            </button>
            <button className="btn btn-ghost" onClick={onCopyKey} disabled={!accessKey}>
              {keyCopied ? 'Copied' : 'Copy key'}
            </button>
            <button className="btn btn-ghost text-red-700" onClick={onRotateKey} disabled={rotating || !accessKey}>
              {rotating ? 'Rotating…' : 'Rotate key'}
            </button>
          </div>

          <details className="text-xs text-ink-muted">
            <summary className="cursor-pointer font-semibold text-navy">Use in MAPTRIUM Desktop</summary>
            <div className="mt-2 space-y-1">
              <div>On first launch, the desktop app asks for your account email and access key.</div>
              <div>It uses them to fetch your signed license file from <code className="font-mono">/api/desktop/my-license</code> and verifies it offline thereafter.</div>
            </div>
          </details>
        </div>
      </main>
    </div>
  );
}
